import { Divider } from '@mui/material';
import React from 'react';
import { usePresentationFormContext } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Presentations/PresentationFormContext';
import { isFieldVisible } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Presentations/constants/formConfigs';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { SlPill } from 'src/components/BeaconV2Components/SlPill';
import { formatTime } from 'src/utils/dateformatting';
import { useAppSelector } from 'src/utils/Hooks';

export const PresentationsConfirmationPage = () => {
  const mainTimePeriod = useAppSelector((state) => state.mainTimePeriod);
  const comparisonTimePeriod = useAppSelector((state) => state.comparisonTimePeriod);
  const userConfig = useAppSelector((state) => state.user.config);

  const {
    type: { watch: typeWatch },
    selections: { watch: selectionsWatch },
    brands: { watch: brandsWatch },
    currentFormConfig
  } = usePresentationFormContext();

  const createdBy = `${userConfig.firstName} ${userConfig.lastName}`;
  const createdDate = formatTime(new Date(), 'MMM D, YYYY');
  const timePeriod = `${mainTimePeriod.shortDisplayName} vs ${comparisonTimePeriod.shortDisplayName}`;

  const reportType = typeWatch('reportType');
  const currency = selectionsWatch('currency');
  const entityType = selectionsWatch('entityType');
  const entityId = selectionsWatch('entityId');
  const retailerIds = selectionsWatch('retailerIds');
  const categoryIds = selectionsWatch('categoryIds');
  const subcategoryIds = selectionsWatch('subcategoryIds');
  const brandGroupings = brandsWatch('brandGroupings');
  const brandIds = selectionsWatch('brandIds');

  // Determine which brand display to use based on config
  const showBrandGroupings = isFieldVisible(currentFormConfig, 'brandGroupings');
  const hasBrandIds = brandIds && brandIds.length > 0;

  const getInclusions = (entityTypeId) => {
    switch (entityTypeId) {
      case 'category':
        return [
          { label: 'Category Inclusions', value: categoryIds },
          { label: 'Subcategory Inclusions', value: subcategoryIds }
        ];
      case 'segment':
        return [{ label: 'Segment Inclusion', value: [entityId] }];
      case 'businessunit':
        return [{ label: 'Business Unit Inclusion', value: [entityId] }];
      default:
        return [];
    }
  };

  const confirmationSections = [
    {
      title: 'Type',
      items: [
        { label: 'Report Type', value: reportType.name },
        { label: 'Created By', value: createdBy },
        { label: 'Created Date', value: createdDate }
      ]
    },
    {
      title: 'Selections',
      items: [
        { label: 'Time Period', value: timePeriod },
        { label: 'Currency', value: currency },
        // Only show entity type if visible in config
        ...(isFieldVisible(currentFormConfig, 'entityType') 
          ? [{ label: 'Data Structure', value: entityType?.name || 'Not selected' }] 
          : []),
        { label: 'Retailer Inclusions', value: retailerIds },
        // Only show entity inclusions if entity type is visible
        ...(isFieldVisible(currentFormConfig, 'entityType') ? getInclusions(entityType?.id) : [])
      ]
    },
    {
      title: 'Brands',
      items: showBrandGroupings
        ? [{ label: 'Brand Selection Type', value: brandGroupings?.name || 'Not selected' }]
        : hasBrandIds
          ? [{ label: 'Selected Brands', value: brandIds }]
          : [{ label: 'Brand Selection', value: 'No brands configured' }]
    }
  ];

  const Section = ({ title, items }) => (
    <Flex flexDirection="column" gap={32}>
      <Flex gap={12} flexDirection="column">
        <Text variant="h4" lineHeight="28px">
          {title}
        </Text>
        <Divider />
      </Flex>
      <Flex flexDirection="column" gap={24}>
        {items.map(({ label, value }) => {
          if (Array.isArray(value) && value.length) {
            return (
              <Flex flexDirection="column" gap={16} key={label}>
                <Text variant="body2" fontWeight="700">
                  {label}:
                </Text>
                <Flex gap={8}>
                  {value.map((item) => (
                    <SlPill
                      key={item.id}
                      sx={{
                        background: '#EDF5FF',
                        border: 'none',
                        padding: '3px 12px',
                        fontSize: '12px',
                        fontWeight: 400,
                        height: 'auto',
                        lineHeight: '18px'
                      }}
                    >
                      {item.name}
                    </SlPill>
                  ))}
                </Flex>
              </Flex>
            );
          } else if (!Array.isArray(value)) {
            return (
              <Flex gap={32} key={label} alignItems="center">
                <Text variant="body2" fontWeight="700" sx={{ width: '140px' }}>
                  {label}:
                </Text>
                <Text variant="body2" lineHeight="20px">
                  {value}
                </Text>
              </Flex>
            );
          }

          return null;
        })}
      </Flex>
    </Flex>
  );

  return (
    <Flex flexDirection="column" gap={64}>
      {confirmationSections.map((section) => (
        <Section key={section.title} title={section.title} items={section.items} />
      ))}
    </Flex>
  );
};
